import React, { useState } from 'react';
import { Chip, Stack, Skeleton, SxProps, Theme } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';

const niceColors = require('nice-color-palettes');

interface ICustomChipGroup {
    tags: string[];
    maxVisible?: number;
    tagSx?: SxProps<Theme>;
    withNiceColor?: boolean;
    onTagClick?: (tag: string) => void;
    clickable?: boolean;
    loading?: boolean;
    withWrap?: boolean;
}

const CustomChipGroup: React.FC<ICustomChipGroup> = ({
    tags,
    maxVisible = 3,
    tagSx,
    withNiceColor,
    onTagClick,
    clickable,
    loading = false,
    withWrap = false,
}) => {
    const { themeStyle } = useThemeContext();
    const [selectedTag, setSelectedTag] = useState<string | null>(tags[0]);

    const colorsNice = [...niceColors[67], ...niceColors[10], ...niceColors[2], ...niceColors[50]];

    const visibleTags = tags ? tags.slice(0, maxVisible) : [];
    const remainingCount = tags ? tags.length - maxVisible : 0;

    const getTagStyle = (tag: string) => {
        return clickable && selectedTag === tag
            ? {
                  color: theme[themeStyle].form_gray,
                  backgroundColor: theme[themeStyle].tag_white,
              }
            : {};
    };

    const handleTagClick = (tag: string) => {
        if (clickable) {
            onTagClick && onTagClick(tag);
            setSelectedTag(tag);
        }
    };

    return (
        <Stack
            direction="row"
            gap={0.5}
            position="relative"
            flexWrap={withWrap ? 'wrap' : 'nowrap'}
            sx={{ display: 'flex' }}>
            {loading
                ? Array.from({ length: maxVisible }).map((_, index) => {
                      const widths = [90, 80, 75, 65, 155]; // You can adjust or randomize more
                      const width = widths[index % widths.length];

                      return (
                          <Skeleton
                              key={index}
                              variant="rounded"
                              width={width}
                              height={28}
                              sx={{
                                  borderRadius: '6px',
                                  ...tagSx,
                              }}
                          />
                      );
                  })
                : visibleTags.map((category, index) => (
                      <Chip
                          label={category}
                          variant="outlined"
                          key={index}
                          size="small"
                          onClick={() => clickable && handleTagClick(category)}
                          sx={{
                              backgroundColor: withNiceColor
                                  ? colorsNice[category?.length]
                                  : theme[themeStyle].darkGray60,
                              color: withNiceColor ? theme[themeStyle].true_black : theme[themeStyle].tag_white,
                              borderRadius: '6px',
                              borderColor: 'transparent',
                              fontWeight: 500,
                              textTransform: 'capitalize',
                              fontFamily: FONT_ENUM.MAIN_MEDIUM,
                              cursor: clickable ? 'pointer' : 'default',
                              fontSize: 10,
                              ':hover': {
                                  backgroundColor: withNiceColor
                                      ? colorsNice[category?.length]
                                      : theme[themeStyle].darkGray60,
                                  color: theme[themeStyle].tag_white,
                              },
                              ...getTagStyle(category),
                              ...tagSx,
                          }}
                      />
                  ))}

            {!loading && remainingCount > 0 && (
                <Chip
                    label={`+${remainingCount} more`}
                    variant="outlined"
                    size="small"
                    sx={{
                        backgroundColor: theme[themeStyle].darkGray60,
                        color: withNiceColor ? theme[themeStyle].true_black : theme[themeStyle].tag_white,
                        borderRadius: '4px',
                        borderColor: 'transparent',
                        fontWeight: 500,
                        textTransform: 'capitalize',
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: 10,
                        ...tagSx,
                    }}
                />
            )}
        </Stack>
    );
};

export { CustomChipGroup };
